import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../app/hooks";
import { AdminUser } from "../models/AdminUser";
import { SetCustomPriceRequest } from "../contracts/SetCustomPriceRequest";
import { setUserCustomPrice } from "../slice-extensions/admin-slice-extension";

interface UseCustomPriceModal {
  customPriceUser: AdminUser | null;
  customPriceBusy: boolean;
  openCustomPrice: (user: AdminUser) => void;
  closeCustomPrice: () => void;
  applyCustomPrice: (request: SetCustomPriceRequest | null) => Promise<void>;
}

/**
 * Orchestrates the admin "custom price" modal: which user (if any) is being
 * repriced, the busy flag while the request is in flight, and the dispatch +
 * success/failure toast around setting or clearing the override.
 */
export function useCustomPriceModal(): UseCustomPriceModal {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [customPriceUser, setCustomPriceUser] = useState<AdminUser | null>(null);
  const [customPriceBusy, setCustomPriceBusy] = useState(false);

  const applyCustomPrice = async (request: SetCustomPriceRequest | null) => {
    if (!customPriceUser) return;
    setCustomPriceBusy(true);
    try {
      await dispatch(
        setUserCustomPrice({ instructorId: customPriceUser.instructorId, request })
      ).unwrap();
      toast.success(t(request ? "admin.customPrice.setSuccess" : "admin.customPrice.clearSuccess"));
      setCustomPriceUser(null);
    } catch (error) {
      toast.error((error as { message?: string })?.message || t("admin.customPrice.failed"));
    } finally {
      setCustomPriceBusy(false);
    }
  };

  return {
    customPriceUser,
    customPriceBusy,
    openCustomPrice: setCustomPriceUser,
    closeCustomPrice: () => setCustomPriceUser(null),
    applyCustomPrice,
  };
}
